import { computed, ref, type ComputedRef, type InjectionKey, type Ref } from 'vue';

export interface AddRecordFilters {
  createdAt?: Date | null;
  revision?: number | null;
}

export interface AddRecordFilterContext {
  activeFilters: Ref<AddRecordFilters>;
  isFilterActive: ComputedRef<boolean>;
  isFilterPanelOpen: Ref<boolean>;
  toggleFilterPanel: () => void;
  closeFilterPanel: () => void;
  applyFilters: (filters: AddRecordFilters) => void;
}

export const AddRecordFilterInjectionKey: InjectionKey<AddRecordFilterContext> =
  Symbol('add-record-filter');

export function hasActiveRecordFilters(filters: AddRecordFilters): boolean {
  return Boolean(filters.createdAt) || Boolean(filters.revision && filters.revision > 0);
}

function filtersKey(filters: AddRecordFilters): string {
  const day = filters.createdAt ? filters.createdAt.getTime() : '';
  const revision = filters.revision && filters.revision > 0 ? filters.revision : '';
  return `${day}|${revision}`;
}

export function createAddRecordFilterContext(
  onApply?: (filters: AddRecordFilters) => void
): AddRecordFilterContext {
  const activeFilters = ref<AddRecordFilters>({});
  const isFilterPanelOpen = ref(false);
  const isFilterActive = computed(() => hasActiveRecordFilters(activeFilters.value));

  function toggleFilterPanel(): void {
    isFilterPanelOpen.value = !isFilterPanelOpen.value;
  }

  function closeFilterPanel(): void {
    isFilterPanelOpen.value = false;
  }

  function applyFilters(filters: AddRecordFilters): void {
    const changed = filtersKey(filters) !== filtersKey(activeFilters.value);
    activeFilters.value = filters;
    isFilterPanelOpen.value = false;

    if (changed) {
      onApply?.(filters);
    }
  }

  return {
    activeFilters,
    isFilterActive,
    isFilterPanelOpen,
    toggleFilterPanel,
    closeFilterPanel,
    applyFilters,
  };
}
